import React, { useState, useMemo } from "react";
import styles from "./highRiskAnalysisTable.module.css";
import ZeroData from "../../zeroData/zeroData";
import InfoIcon from "../../../assets/icons/info-icon.svg";
import SearchIcon from "../../../assets/icons/search-icon.svg";

const HighRiskAnalysisTable = ({ highRiskAnalysis = [] }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [showPopover, setShowPopover] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // 'asc' | 'desc'

  const validHighRiskAnalysis = useMemo(() => {
    return highRiskAnalysis
      .slice()
      .filter((risk) =>
        (risk.className || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
      .sort((a, b) => {
        const cmp = (a.className || "").localeCompare(b.className || "");
        return sortOrder === "asc" ? cmp : -cmp;
      });
  }, [highRiskAnalysis, searchTerm, sortOrder]);

  const totalPages = useMemo(
    () => Math.ceil(validHighRiskAnalysis.length / itemsPerPage),
    [validHighRiskAnalysis.length, itemsPerPage]
  );

  const currentTableData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return validHighRiskAnalysis.slice(start, start + itemsPerPage);
  }, [currentPage, itemsPerPage, validHighRiskAnalysis]);

  const handlePageClick = (page) => setCurrentPage(page);

  if (!validHighRiskAnalysis.length) {
    return (
      <ZeroData>
        <h4>Nenhuma análise de risco encontrada.</h4>
      </ZeroData>
    );
  }

  return (
    <div className={styles.highRiskAnalysisTableContainer}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2>Maior Probabilidade de Inadimplência • Cursos</h2>
          <div style={{ position: "relative" }}>
            <img
              src={InfoIcon}
              alt="Informação"
              onClick={() => setShowPopover(!showPopover)}
              style={{
                width: "14px",
                height: "14px",
                cursor: "pointer",
                verticalAlign: "middle",
                display: "inline-block",
              }}
            />
            {showPopover && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "100%",
                  transform: "translate(0, -50%)",
                  marginLeft: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #ccc",
                  padding: "10px",
                  zIndex: 100,
                  width: "220px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                  borderRadius: "4px",
                }}
              >
                <p style={{ margin: 0, fontSize: "12px" }}>
                  Análise dos cursos com maior risco financeiro, mostrando estudantes em alto risco, valores em atraso e exposição total ao risco.
                </p>
                <button
                  onClick={() => setShowPopover(false)}
                  style={{
                    marginTop: "8px",
                    fontSize: "10px",
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    color: "#0962AD",
                  }}
                >
                  Fechar
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.headerRight}>
          {/* Busca */}
          <div className={styles.searchWrapper}>
            <img src={SearchIcon} alt="Buscar" className={styles.searchIcon} />
            <input
              type="text"
              placeholder="Pesquisar..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className={styles.searchInput}
            />
          </div>

          {/* Select de ordenação */}
          <div>
            <select
              className={styles.orderWrapper}
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="desc">Ordem Decrescente</option>
              <option value="asc">Ordem Crescente</option>
            </select>
          </div>
        </div>
      </div>

      <table className={styles.highRiskAnalysisTable}>
        <thead>
          <tr>
            <th>Curso</th>
            <th>Estudantes de Alto Risco</th>
            <th>Valor em Atraso (R$)</th>
            <th>Exposição ao Risco (R$)</th>
          </tr>
        </thead>
        <tbody>
          {currentTableData.map((risk, idx) => (
            <tr key={idx}>
              <td>{risk.className}</td>
              <td>{risk.highRiskStudents}</td>
              <td>R$ {risk.totalOverdueValue.toLocaleString('pt-BR')}</td>
              <td>R$ {risk.weightedRiskExposure.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className={styles.pagination}>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            className={page === currentPage ? styles.activePage : ""}
            onClick={() => handlePageClick(page)}
          >
            {page}
          </button>
        ))}
      </div>
    </div>
  );
};

export default HighRiskAnalysisTable;
